import { useDispatch, useSelector } from "react-redux";
import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import { useEffect, useState } from "react";
import { useToasts } from "react-toast-notifications";
import { useNavigate } from "react-router";

const usePreview = () => {
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const dispatch = useDispatch();

  const [createstatus, setCreateStatus] = useState("");
  const [show, setShow] = useState(false);
  const [imageInputFile, setImageInputFile] = useState([]);
  const [imageLoader, setImageLoader] = useState(false);
  const [openImage, setOpenImage] = useState(false);
  const [tempImagesData, setTempImagesData] = useState([]);
  const [loadingTempImages, setLoadingTempImages] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalSelection, setTotalSelection] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submitionLoading, setSubmitionLoading] = useState(false);
  const [invalidFiles, setInvalidFiles] = useState([]);
  const [showError, setShowError] = useState(false);
  const [viewType, setViewType] = useState("content");
  const [uploadError, setUploadError] = useState({ msg: "", errors: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);
  const [acceptLoader, setAcceptLoader] = useState(false);
  const [rejectLoader, setRejectLoader] = useState(false);

  const openModal = (imageUrl) => {
    setModalImageUrl(imageUrl);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setModalImageUrl(null);
  };

  async function getBulkImages() {
    try {
      setLoadingTempImages(true);
      const result = await apiHandler({
        url: `${endpoint.GET_TEMP_IMAGES}?iPageNumber=${currentPage}&iPageSize=10`,
        method: "GET",
        authToken: authData,
        storeId,
        dispatch,
      });

      if (result?.data?.status === "success") {
        const fetchedImages = result?.data?.data?.productData;

        setTempImagesData(fetchedImages);
        const totalPageCount = result?.data?.data?.totalRecords;
        setTotalPages(totalPageCount);
      }
    } catch (error) {
      console.log("Error fetching images", error);
    } finally {
      setLoadingTempImages(false);
    }
  }

  useEffect(() => {
    getBulkImages();
  }, [currentPage]);

  const uploadImages = async () => {
    if (!imageInputFile) return;

    try {
      setImageLoader(true);

      const formData = new FormData();

      // Append each image from imageInputFile array to formData
      imageInputFile.forEach((file) => {
        formData.append("productImages", file); // Append each image file with the same key
      });

      const response = await apiHandler({
        url: endpoint.IMAGES_UPLOAD,
        authToken: authData,
        method: "POST",
        data: formData,
        storeId,
        dispatch,
      });

      setOpenImage(false);
      if (response.data.status === "success") {
        handleClose();
        setOpenImage(false);
        navigating();
        setImageInputFile([]);
        setInvalidFiles([]);
        addToast(response.data.message, {
          appearance: "success",
          autoDismiss: true,
        });
      } else {
        if (response?.data?.data?.invalidFiles) {
          const invalidFilesResponse = response?.data?.data?.invalidFiles;
          const totalFiles = [...imageInputFile, ...invalidFiles];
          const matchingFiles = totalFiles.filter((file) =>
            invalidFilesResponse.includes(file?.name)
          );
          setOpenImage(false);
          handleClose();
          navigating();
          setShowError(true);
          setUploadError({
            msg: response?.data?.message,
            errors: matchingFiles,
          });
        }
      }
    } catch (error) {
      console.log("Error in bulk upload products", error);
    } finally {
      setImageLoader(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setShowConfirmation(false);
  };

  const navigating = () => {
    setTotalSelection([]);
    setTotalPages(0);
    setCurrentPage(1);
    setTempImagesData([]);
    getBulkImages();
    handleClose();
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSelected = (e, id) => {
    setTotalSelection((prev) => {
      const isSelected = prev.find((item) => item === id);
      if (isSelected) {
        return prev.filter((item) => item !== id);
      } else {
        return [...totalSelection, id];
      }
    });
  };

  const handleAllSelection = (event) => {
    const isChecked = event.target.checked;

    const allIds = tempImagesData.flatMap((product) =>
      JSON.parse(product.ImageJson).map((image) => image.iTempProductGalleryId)
    );

    if (isChecked) {
      // Select all checkboxes
      setTotalSelection(allIds);
    } else {
      // Deselect all checkboxes
      setTotalSelection([]);
    }
  };

  const handleSubmit = async (type, loaderFunc) => {
    try {
      loaderFunc(true);
      setShowConfirmation(false);

      const submitData = {
        aAcceptJson:
          type === "accept"
            ? totalSelection.map((temp) => ({
                iTempProductGalleryId: temp,
              }))
            : [],
        aRejectJson:
          type !== "accept"
            ? totalSelection.map((temp) => ({
                iTempProductGalleryId: temp,
              }))
            : [],
      };

      const response = await apiHandler({
        url: endpoint.UPDATE_TEMP_IMAGES,
        method: "POST",
        authToken: authData,
        data: submitData,
        storeId,
        dispatch,
      });

      if (response?.data?.status === "success") {
        setTempImagesData([]);
        setTotalSelection([]);
        let message =
          type === "accept"
            ? submitData.aAcceptJson.length == 1
              ? "Image accepted successfully"
              : "Images accepted successfully"
            : submitData.aRejectJson.length == 1
            ? "Image rejected successfully"
            : "Images rejected successfully";
        addToast(message, {
          appearance: "success",
          autoDismiss: true,
        });
        loaderFunc(false);
        let totalImages = 0;
        tempImagesData.forEach((product) => {
          const images = JSON.parse(product.ImageJson);
          totalImages += images.length;
        });
        if (
          (tempImagesData.length === 1 ||
            totalImages === submitData.aAcceptJson.length) &&
          type === "accept"
        ) {
          navigate("/unusedimages");
        } else {
          navigate("/productsandbox");
        }

        await getBulkImages();
      }
    } catch (error) {
      addToast("Something went wrong try again later", {
        appearance: "error",
        autoDismiss: true,
      });
      console.log(`Error during submission: ${error}`);
    } finally {
      loaderFunc(false);
    }
  };

  const closeErrorDialog = () => {
    setShowError(false);
    setInvalidFiles([]);
    setImageInputFile([]);
  };

  return {
    uploadImages,
    handleClose,
    show,
    createstatus,
    imageLoader,
    tempImagesData,
    loadingTempImages,
    currentPage,
    totalPages,
    handlePageChange,
    totalSelection,
    handleAllSelection,
    viewType,
    setViewType,
    showConfirmation,
    handleSubmit,
    submitionLoading,
    showError,
    uploadError,
    closeErrorDialog,
    handleSelected,
    isModalOpen,
    modalImageUrl,
    closeModal,
    openModal,
    acceptLoader,
    rejectLoader,
    setAcceptLoader,
    setRejectLoader,
  };
};

export default usePreview;
